/**
 * reportChartLayoutTokens — K4 (richer, never-empty charts).
 *
 * THE single source of truth for the report charts' GEOMETRY — the
 * min-height / aspect bounds the {@link ChartFrame} reserves, the radar SVG
 * sizes + ring radii, the proportional-bar heights (stack-bar, scale-meter),
 * the recharts plot-area insets (margins), and the small visual constants
 * (tick / dot / vertex-cap sizes). Before K4 these were scattered magic
 * numbers inlined per-component — `aspect={0.46}`, `minHeight={260}`,
 * `outerRadius="68%"`, `rMax = size * 0.42`, `height = 30`, `height = 8`,
 * `margin={{ top: 16, right: 28, … }}` — that drifted independently and gave
 * the charts their flat, under-considered proportions.
 *
 * WHY THIS EXISTS
 * ---------------
 * K4 raises the chart CRAFT to the redesign reference's bar (deliberate
 * proportions, breathing room, dense-but-legible plot areas) while keeping
 * OUR fonts + OUR theme palette (`resolveReportChartPalette` / `reportChartColor`
 * + `roleColor`). The visual lift is mostly a matter of PROPORTION — taller,
 * less letter-boxed canvases; radar rings sized so the polygon reads; bars
 * with enough height to carry an in-bar label; insets that give the axes /
 * tick labels room instead of crowding the plot. Tokenizing that geometry in
 * one file means every chart shares the same deliberate rhythm and a future
 * proportion change moves them in lockstep (the same discipline
 * `reportLayoutTokens` brought to page geometry).
 *
 * NOT DATA — these are VISUAL constants only. K4 NEVER fabricates data; the
 * empty / placeholder states stay honest (the J6 radar tier-midpoint helpers
 * and the ClimateProfileChart "refreshing" placeholder are preserved). These
 * tokens only shape how the POPULATED chart is drawn.
 *
 * UNITS — pixels for SVG / DOM heights and recharts margins (the medium's
 * native unit); a unitless ratio for the {@link ChartFrame} aspect; a 0..1
 * fraction for radii expressed as a share of the SVG box; percentage strings
 * for the recharts `outerRadius` prop (its native form). Pure data — no React,
 * no side effects — so it is safe in render and trivially unit-testable.
 */

/**
 * {@link ChartFrame} fluid-height bounds for the report's recharts canvases.
 *
 * `aspect` is height : width — the reserved height is `width * aspect`, clamped
 * to [`minHeight`, `maxHeight`]. K4 lifts the prior squat `aspect=0.46` /
 * `minHeight=260` to a less letter-boxed canvas so the plotted series + axes +
 * legend have deliberate breathing room at the fixed ~688px print column
 * (matching the reference's generous figure frames) without ballooning on a
 * wide desktop column (`maxHeight` cap unchanged in spirit).
 *
 * Three named profiles cover the report's chart shapes:
 *   - `combo`  — the multi-series ComposedChart (ClimateProfileChart): bars +
 *                two temperature lines + dual axes + a legend below. Needs the
 *                most vertical room so the lines read distinctly over the bars.
 *   - `bars`   — the horizontal range/category BarCharts (LcoeChartSection,
 *                ScoreDriverWaterfall): one row per category, height scales with
 *                the row count but the frame keeps a deliberate floor.
 *   - `radar`  — the recharts RadarChart (FeasibilityProfileSection): near-square
 *                so the polygon + vertex labels are not squashed.
 */
export interface ChartFrameBounds {
  /** Height : width ratio the frame reserves from the observed width. */
  readonly aspect: number;
  /** Lower clamp (px) — the chart never gets squatter than this. */
  readonly minHeight: number;
  /** Upper clamp (px) — the chart never balloons on a wide column. */
  readonly maxHeight: number;
  /**
   * SHRINK-TO-FIT (RC-2) — the HARD legibility floor (px). When a paginator
   * passes a `fitMaxHeight` to shrink a chart down to fill a page foot, the
   * reserved height is NEVER reduced below this value — below it the axis ticks
   * / dual-axis labels / vertex caps collide and the chart reads as crushed.
   * The shrink only removes vertical PLOT slack; the {@link CHART_INK} font
   * sizes stay constant, so this floor is what protects legibility. If the
   * remaining page space is below `legibilityFloor + caption`, the chart takes
   * the full break to the next page instead (the correct sub-floor fallback).
   */
  readonly legibilityFloor: number;
}

/**
 * Per-shape {@link ChartFrame} bounds. K4: taller, less letter-boxed than the
 * prior inline `aspect=0.46 / minHeight=260 / maxHeight=360` so each canvas
 * carries its series + axes + legend with the reference's deliberate density.
 *
 * SHRINK-TO-FIT (RC-2) — the `maxHeight` caps are nudged DOWN modestly from the
 * prior uniform 380 (combo 340, bars 330, radar 350) so a chart reserves a bit
 * less vertical space and is LESS LIKELY to overflow a page foot in the first
 * place (closing white gaps WITHOUT a risky measured two-pass re-raster). The
 * caps stay comfortably above each `legibilityFloor` (240/240/260) and above
 * the `minHeight` floor, so axis ticks / dual axes / vertex caps stay crisp;
 * the {@link CHART_INK} font sizes are unchanged. `legibilityFloor` is the hard
 * lower bound a future measured `fitMaxHeight` shrink may approach but never
 * cross.
 */
export const CHART_FRAME_BOUNDS: Readonly<Record<'combo' | 'bars' | 'radar', ChartFrameBounds>> = {
  // Combo (climate): the densest figure — give it the most height so the
  // dry-bulb / wet-bulb lines sit clearly above the precipitation bars. Floor
  // 240 keeps the 12-month X ticks (denseTickFontSize 9) + dual axes + below-
  // plot legend from colliding.
  combo: { aspect: 0.52, minHeight: 300, maxHeight: 340, legibilityFloor: 240 },
  // Horizontal bars (LCOE): a deliberate floor so 4 tech rows + the grid
  // reference line + the axis read without crowding. Floor 240 == DEFAULT_MIN.
  bars: { aspect: 0.5, minHeight: 280, maxHeight: 330, legibilityFloor: 240 },
  // Radar: near-square so the polygon + the outer vertex caps have room.
  // DEFECT-A (Option-1) — the radar now sits in a ~46% / ~240-300px LEFT column
  // beside the band legend (not a full-width stack), so the reserved height is
  // trimmed (minHeight 280→240, maxHeight 350→320) to keep the side-by-side
  // exhibit compact (~320px) and clear of the page foot. Floor stays at the
  // 240 shared minimum — below it the bottom vertex caps (Logistics/Cooling)
  // begin to clip — and remains ≥ the combo/bars floors.
  radar: { aspect: 0.78, minHeight: 240, maxHeight: 320, legibilityFloor: 240 },
} as const;

/**
 * recharts plot-area margins (px), top → right → bottom → left, per shape.
 * K4 widens these from the prior crowded inline values so axis labels, the
 * angled month ticks, the dual-axis unit captions, and the grid-reference
 * line label all have room — the plot no longer butts against the frame edge.
 */
export interface ChartPlotMargin {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const CHART_PLOT_MARGIN: Readonly<Record<'combo' | 'lcoe' | 'radar' | 'waterfall', ChartPlotMargin>> = {
  // Combo (climate): room at the right for the precip axis unit + below for the
  // angled 12-month ticks + the legend.
  combo: { top: 20, right: 32, bottom: 30, left: 10 },
  // LCOE horizontal bars: extra right inset for the grid-retail reference-line
  // label, generous top for that label's `position:'top'`.
  lcoe: { top: 34, right: 84, bottom: 32, left: 12 },
  // Radar: symmetric inset so the bottom / side vertex caps never clip.
  radar: { top: 18, right: 28, bottom: 18, left: 28 },
  // Waterfall: room below for the "Impact (points)" axis label.
  waterfall: { top: 10, right: 28, bottom: 26, left: 10 },
} as const;

/**
 * The recharts RadarChart `outerRadius` (its native percentage-of-box form).
 * K4 nudges the polygon slightly inward from the prior `68%` so the outer
 * vertex caps (PolarAngleAxis tick labels) sit fully inside the SVG box at the
 * narrow print column instead of clipping at the edge.
 */
export const RADAR_OUTER_RADIUS = '66%' as const;

/**
 * EditorialRadarPanel (pure inline-SVG radar) geometry.
 *
 * `size` is the SVG viewBox side; `rMaxFraction` is the outer-ring radius as a
 * share of `size` (the data polygon + rings scale to this); `capInset` is the
 * px gap between the outer ring and the mono vertex caps; `ringFractions` are
 * the concentric ring radii as shares of the outer ring (inner → outer is the
 * reverse). K4 adds a 4th, fainter ring and a center pip for a more crafted,
 * instrument-panel scaffold (denser rings read as a graticule, not a sparse
 * triangle) — purely visual; the data polygon is unchanged.
 */
export const RADAR_PANEL = {
  /** SVG viewBox side (px). */
  size: 300,
  /** Outer-ring radius as a fraction of `size`. */
  rMaxFraction: 0.42,
  /** px gap from the outer ring to the mono vertex caps. */
  capInset: 14,
  /** Concentric ring radii as fractions of the outer ring (outer → inner). */
  ringFractions: [1, 0.75, 0.5, 0.25] as readonly number[],
  /** Mono vertex-cap font size (px). */
  capFontSize: 8.5,
  /** Data-polygon stroke width (px). */
  strokeWidth: 1.8,
  /** Vertex dot radius (px) on the data polygon — K4 marks each axis value. */
  vertexDotRadius: 2.4,
  /** Center pip radius (px) — anchors the graticule. */
  centerPipRadius: 1.4,
} as const;

/**
 * Proportional horizontal-bar heights (px) — EditorialStackBar (segmented
 * supply/balance/cost strip) + EditorialScaleMeter (the firm-vs-need adequacy
 * gauge that reads as the redesign's "available + shortfall" bar).
 *
 * K4 lifts the stack-bar from a thin 30px to a more substantial 34px so the
 * in-bar mono labels (e.g. "Grid 180 MW") sit with comfortable vertical
 * padding (the reference's bars are tall, deliberate blocks, not thin rules).
 * The scale-meter `fill` track grows to 28px so the in-fill label ("≈50 MW
 * available") reads, while the `gradient` go/no-go track stays a thin 8px rule.
 */
export const BAR_HEIGHT = {
  /** EditorialStackBar default segment-strip height. */
  stack: 34,
  /** EditorialScaleMeter `fill` (adequacy/shortfall) track height. */
  scaleFill: 28,
  /** EditorialScaleMeter `gradient` (go/no-go) track height. */
  scaleGradient: 8,
} as const;

/**
 * Recharts tick / dot visual constants, shared so the charts read uniformly.
 * K4 keeps these conservative (legibility at the narrow print column) but
 * names them so they stop being per-chart magic numbers.
 */
export const CHART_INK = {
  /** Axis tick font size (px) for the standard numeric axes. */
  axisTickFontSize: 12,
  /** Smaller tick font (px) for the dense 12-month X axis / radial ring band. */
  denseTickFontSize: 11,
  /** Axis unit / inline label font size (px). */
  axisLabelFontSize: 11,
  /** Legend font size (px). */
  legendFontSize: 12,
  /** Line-series dot radius (px) on the highlighted (wet-bulb) line. */
  lineDotRadius: 2,
} as const;
